import React, {
  ForwardRefExoticComponent,
  forwardRef,
  useEffect,
  useState,
  useCallback,
} from 'react';
import Avatar, {
  AvatarProps as MuiAvatarProps,
} from '@material-ui/core/Avatar';
import Skeleton, {
  SkeletonProps as MuiSkeletonProps,
} from '@material-ui/lab/Skeleton';
import useIsMounted from 'ismounted';
import clsx from 'clsx';
import makeStyles from '@material-ui/core/styles/makeStyles';
import pick from 'lodash/pick';
import { fade } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  skeleton: {
    backgroundColor: `${fade(
      theme.palette.text.primary,
      theme.palette.type === 'light' ? 0.11 : 0.13,
    )} !important`,
    maxWidth: 'none',
    '& > img': {
      display: 'none',
    },
  },
}));

const skeletonClassKeys = [
  'root',
  'text',
  'rect',
  'circle',
  'pulse',
  'wave',
  'withChildren',
  'fitContent',
  'heightAuto',
];

export type AppAvatarProps = MuiAvatarProps & {
  isVisible?: boolean,
  loaded?: boolean,
  SkeletonProps?: MuiSkeletonProps,
  AvatarOnlyProps?: Omit<MuiAvatarProps, 'src' | 'children' | 'variant'>,
  disableFallback?: boolean,
  id?: string,
};

const AppAvatar: ForwardRefExoticComponent<AppAvatarProps> = forwardRef(
  (
    {
      isVisible = true,
      variant = 'circular',
      loaded = false,
      imgProps: { onLoad, onError, ...otherImgProps } = {},
      src,
      style,
      className,
      classes: classesProps,
      children,
      SkeletonProps: {
        className: skeletonClassNameProps,
        classes: skeletonClassesProps,
        style: skeletonStyleProps,
        ...skeletonProps
      } = {},
      AvatarOnlyProps: {
        className: avatarOnlyClassNameProps,
        classes: avatarOnlyClassesProps,
        style: avatarOnlyStyleProps,
        imgProps: avatarOnlyImgProps,
        ...avatarOnlyProps
      } = {},
      disableFallback,
      id,
      ...AvatarProps
    }: AppAvatarProps,
    ref,
  ) => {
    const classes = useStyles();

    const [complete, setComplete] = useState(false);
    const isMounted = useIsMounted();

    const handleLoad = useCallback(
      (event) => {
        if (isMounted.current) {
          onLoad?.(event);
          setComplete(true);
        }
      },
      [isMounted, onLoad, setComplete],
    );

    const handleError = useCallback(
      (event) => {
        if (isMounted.current) {
          onError?.(event);
          setComplete(true);
        }
      },
      [isMounted, onError, setComplete],
    );

    useEffect(() => {
      loaded && isVisible && !src && setComplete(true);
    }, [loaded, isVisible, src, setComplete]);

    return (
      <Avatar
        {...AvatarProps}
        {...(complete && avatarOnlyProps)}
        src={src}
        className={clsx(className, complete && avatarOnlyClassNameProps)}
        style={{ ...style, ...(complete && avatarOnlyStyleProps) }}
        classes={{ ...classesProps, ...(complete && avatarOnlyClassesProps) }}
        children={complete && children}
        variant={variant}
        {...(!complete && {
          component: (props) => (
            <Skeleton
              {...props}
              variant={
                ['circle', 'circular'].includes(variant) ? 'circle' : 'rect'
              }
              {...skeletonProps}
              className={clsx(
                props.className,
                className,
                skeletonClassNameProps,
              )}
              classes={{
                ...props.classes,
                ...pick(classesProps, skeletonClassKeys),
                ...skeletonClassesProps,
                root: clsx(
                  classes.skeleton,
                  props.classes?.root,
                  classesProps?.root,
                  skeletonClassesProps?.root,
                ),
              }}
              style={{ ...props.style, ...style, ...skeletonStyleProps }}
            />
          ),
        })}
        imgProps={{
          ...otherImgProps,
          ...(complete && avatarOnlyImgProps),
          onLoad: handleLoad,
          ...(!disableFallback && { onError: handleError }),
        }}
        ref={ref}
        id={id}
      />
    );
  },
);

export default AppAvatar;
